<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance, onMounted } from 'vue';

import { useTiming } from "@/mixins/timing";
import { isTouchEnabled } from "@/mixins/device";

const { debounce } = useTiming();

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  debouncing: {
    type: Boolean,
    default: false,
  },
  focus: {
    type: Boolean,
    default: true,
  },
  small: {
    type: Boolean,
    default: false,
  },
  fullW: {
    type: Boolean,
    default: false,
  },
  hasSibling: {
    type: Boolean,
    default: false,
  },
  transparent: {
    type: Boolean,
    default: false,
  },
  gray: {
    type: Boolean,
    default: false,
  },
});

const instanceId = getCurrentInstance()?.uid || Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000; // with fallback
const emit = defineEmits(["update:modelValue", "focus", "blur"]);
const inputValue = ref(props.modelValue);

watch(props, () => {
  if (props.modelValue != inputValue.value) {
    inputValue.value = props.modelValue;
  } else if (props.focus) {
    focusSearch();
  }
});

const mainClasses = computed(() => {
  let classList = "";
  classList += props.fullW ? 'form-item--full-w ' : '';
  classList += props.small ? 'form-item--search--small ' : '';
  classList += props.hasSibling ? 'form-item--search--has-sibling ' : '';
  classList += props.gray ? 'form-item--search--gray ' : '';
  return classList;
});

function onChange() {
  if (props.debouncing) {
    debouncedEmit();
  } else {
    emit("update:modelValue", inputValue.value);
  }
};

function onFocus() {
  emit("focus");
};

function onBlur() {
  emit("blur");
};

const debouncedEmit = debounce(function () {
  emit("update:modelValue", inputValue.value);
}, 300);

function focusSearch() {
  if (!props.focus) {
    return false;
  }

  const searchField = document.getElementById(`search-${instanceId}`);
  if (!isTouchEnabled() && searchField) {
    setTimeout(() => {
      searchField.focus();
    }, 10);
  }
}

onMounted(() => {
  if (props.focus) {
    focusSearch();
  }
});

</script>
<template>
  <div class="form-item form-item--search" :class="mainClasses">
    <input
      v-model="inputValue"
      @input="onChange"
      @focus="onFocus"
      @blur="onBlur"
      type="search"
      :id="`search-${instanceId}`"
      :placeholder="$t('ui.search')"
    />
    <i class="fa-light fa-search form-item--search__icon-search" />
  </div>
</template>
